<script setup lang="ts">
import type { Project } from '#auth/composables/useAuth'
import { MemberStatusEnum } from '@forgd/shared/enums'
import { ProjectAvatar } from '@forgd/ui-kit/blocks/avatars'
import { Button } from '@forgd/ui-kit/button'
import { Dialog, DialogContent, DialogTitle } from '@forgd/ui-kit/dialog'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { ChevronRightIcon } from '@heroicons/vue/24/outline'

interface ProjectEditorAccess {
  isActiveEditor: boolean
}

const auth = useAuth()
const { isOpen, selectProject, goToDashboard, close } = useSelectProjectModal()

const editorAccessByProjectId = ref<Record<string, ProjectEditorAccess>>({})
const isLoadingEditors = ref(false)

const eligibleOrganizations = computed(() =>
  (auth.organizations || []).filter(org => org.memberStatus === MemberStatusEnum.Active && org.projects.length > 0),
)

const projectRows = computed(() =>
  eligibleOrganizations.value.flatMap(org =>
    org.projects.map(project => ({
      org,
      project,
    })),
  ),
)

const selectableProjectRows = computed(() =>
  projectRows.value.filter(({ project }) =>
    editorAccessByProjectId.value[project.id]?.isActiveEditor === true,
  ),
)

const skeletonRowCount = 3

function onSelectProject(project: Project) {
  selectProject(project)
}

async function loadEditorAccess() {
  const projectIds = projectRows.value.map(({ project }) => project.id)
  if (projectIds.length === 0) {
    editorAccessByProjectId.value = {}
    return
  }

  isLoadingEditors.value = true
  try {
    const results = await Promise.all(
      projectIds.map(async (projectId) => {
        try {
          const data = await apiFetch('/project/{projectId}/editors', {
            method: 'get',
            params: {
              path: { projectId },
            },
          })
          return [
            projectId,
            {
              isActiveEditor: data.isActiveEditor,
            },
          ] as const
        }
        catch {
          return [
            projectId,
            {
              isActiveEditor: false,
            },
          ] as const
        }
      }),
    )

    editorAccessByProjectId.value = Object.fromEntries(results)
  }
  finally {
    isLoadingEditors.value = false
  }
}

function resetEditorAccess() {
  editorAccessByProjectId.value = {}
  isLoadingEditors.value = false
}

function onOpenChange(open: boolean) {
  if (!open) {
    close()
  }
}

watch(isOpen, async (open) => {
  if (!open) {
    resetEditorAccess()
    return
  }
  await loadEditorAccess()
})
</script>

<template>
  <Dialog :open="isOpen" @update:open="onOpenChange">
    <DialogContent class="max-w-[476px] w-full gap-5 rounded-lg p-5">
      <DialogTitle class="text-xl font-semibold text-primal">
        Select a project to continue
      </DialogTitle>

      <div class="flex max-h-[min(60vh,480px)] flex-col gap-2 overflow-y-auto">
        <div
          v-if="isLoadingEditors"
          class="flex flex-col gap-2"
        >
          <div
            v-for="index in skeletonRowCount"
            :key="index"
            class="flex w-full items-center justify-between rounded p-2"
          >
            <div class="flex items-center gap-3">
              <Skeleton class="size-8 shrink-0 bg-neutral-200" />
              <div class="flex flex-col gap-1">
                <Skeleton class="h-[19px] w-[65px] bg-neutral-200" />
                <Skeleton class="h-[14px] w-[85px] bg-neutral-200" />
                <Skeleton class="h-[14px] w-[147px] bg-neutral-200" />
              </div>
            </div>
            <ChevronRightIcon class="size-4 shrink-0 text-secondary" />
          </div>
        </div>

        <template v-else>
          <button
            v-for="{ org, project } in selectableProjectRows"
            :key="project.id"
            type="button"
            class="flex w-full items-center justify-between rounded p-2 hover:bg-neutral-200"
            @click="onSelectProject(project)"
          >
            <div class="flex items-center gap-3 text-left">
              <ProjectAvatar :src="project.image" size="md" />
              <div class="flex flex-col">
                <div class="text-sm font-semibold uppercase text-primal">
                  {{ project.ticker }}
                </div>
                <div class="text-xs text-secondary">
                  {{ project.name }}
                </div>
                <div class="text-xs text-accent">
                  <template v-if="project.isResearch">
                    Researcher account
                  </template>
                  <template v-else-if="auth.isAdvisor">
                    Advisor | {{ project.tokenListed ? 'Listed Token' : 'Pre-TGE Token' }}
                  </template>
                  <template v-else>
                    {{ org.ownerUserId === auth.me?.id ? 'Token Admin' : 'Teammate' }}
                  </template>
                </div>
              </div>
            </div>
            <ChevronRightIcon class="size-4 shrink-0" />
          </button>
        </template>
      </div>

      <Button variant="outline" block class="h-12" @click="goToDashboard">
        Go to Your Dashboard
      </Button>
    </DialogContent>
  </Dialog>
</template>
